import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, of } from 'rxjs';
import { environment } from '@env/environment';
import {
  IClaim,
  ICreateClaim2Info,
  IInitialClaimInfo,
  IUploadClaimFile,
  IUploadClaimFileResponse,
} from '@core/interfaces/claim.Interface';
import { ResponseData } from '@core/types/types';

@Injectable({
  providedIn: 'root',
})
export class ClaimService {
  private readonly apiUrl = environment.apiUrl;
  private readonly claimsSource$ = of([]);

  private readonly _claim = signal<IClaim | undefined>(undefined);
  private readonly _createClaimInfo = signal<ICreateClaim2Info>({});
  private readonly _initialClaimInfo = signal<IInitialClaimInfo | undefined>(
    undefined
  );

  claim = this._claim.asReadonly();
  initialClaimInfo = this._initialClaimInfo.asReadonly();
  createClaimInfo = this._createClaimInfo.asReadonly();

  constructor(private readonly http: HttpClient) {}

  createClaim(
    token: string
  ): Observable<ResponseData<{ solicitudReclamoId: number }>> {
    return this.http.post(
      `${this.apiUrl}/api/v1/solicitar-reclamo`,
      this.createClaimInfo(),
      {
        headers: { token: token },
      }
    ) as Observable<ResponseData<{ solicitudReclamoId: number }>>;
  }

  completePreauthorizedClaim(
    preauthorizedClaimId: string,
    token: string
  ): Observable<ResponseData<{ solicitudReclamoId: number }>> {
    return this.http.post(
      `${this.apiUrl}/api/v1/autorizacion/completar-reclamo-preautorizado/${preauthorizedClaimId}`,
      this.createClaimInfo(),
      {
        headers: { token: token },
      }
    ) as Observable<ResponseData<{ solicitudReclamoId: number }>>;
  }

  createPreauthorizedClaim(
    token: string
  ): Observable<
    ResponseData<{ solicitudReclamoId: number; preauthorizedClaimId: any }>
  > {
    return this.http.post(
      `${this.apiUrl}/api/v1/solicitar-reclamo-preautorizado`,
      this.createClaimInfo(),
      {
        headers: { token: token },
      }
    ) as Observable<
      ResponseData<{ solicitudReclamoId: number; preauthorizedClaimId: any }>
    >;
  }

  voidClaim(
    providerSlug: string,
    requestClaimId: number,
    reason: string,
    token: string
  ): Observable<ResponseData<any>> {
    return this.http.post(
      `${this.apiUrl}/api/v1/autorizacion/anular/${providerSlug}/${requestClaimId}`,
      { motivoAnulacion: reason },
      {
        headers: { token: token },
      }
    ) as Observable<ResponseData<any>>;
  }

  setClaim(claim: IClaim) {
    this._claim.update((value) => ({ ...value, ...claim }));
  }

  getClaim(): IClaim | undefined {
    return this.claim();
  }

  getClaimByClaimId(id: number): Observable<IClaim | undefined> {
    return this.claimsSource$.pipe(
      map((list) => list.find((claim: any) => claim.claimId == id))
    );
  }

  setInitialClaimInfo(InitialClaimInfo: IInitialClaimInfo) {    
    sessionStorage.setItem(
      'initialClaimInfo',
      JSON.stringify(InitialClaimInfo)
    );
    this._initialClaimInfo.update((value) => ({
      ...value,
      ...InitialClaimInfo,
    }));
  }

  getInitialClaimInfo(
    payloadId?: string
  ): Observable<ResponseData<IInitialClaimInfo>> {
    if (sessionStorage.getItem('initialClaimInfo')) {
      return of({
        data: JSON.parse(sessionStorage.getItem('initialClaimInfo')!),
      } as ResponseData<IInitialClaimInfo>);
    } else {
      return this.http.get(
        `${this.apiUrl}/autorizacion/buscar-info-proceso/${payloadId}`
      ) as Observable<ResponseData<IInitialClaimInfo>>;
    }
  }

  getInitialPreauthorizedClaimInfo(
    preauthorizedClaimId?: string,
    token?: string
  ): Observable<ResponseData<IInitialClaimInfo>> {
    if (sessionStorage.getItem('initialClaimInfo')) {
      return of({
        data: JSON.parse(sessionStorage.getItem('initialClaimInfo')!),
      } as ResponseData<IInitialClaimInfo>);
    } else {
      return this.http.get(
        `${this.apiUrl}/api/v1/autorizacion/buscar-info-proceso-preautorizado/${preauthorizedClaimId}`,
        {
          headers: { token: token! },
        }
      ) as Observable<ResponseData<IInitialClaimInfo>>;
    }
  }

  updateCreateClaimInfo(data: ICreateClaim2Info) {
    this._createClaimInfo.update((info) => ({ ...info, ...data }));
  }

  getCreatedClaimInfo(
    providerSlug: string,
    claimId: number,
    token: string
  ): Observable<ResponseData<IClaim>> {
    return this.http.get(
      `${this.apiUrl}/autorizacion/consultar/${providerSlug}/${claimId}`,
      {
        headers: { token: token },
      }
    ) as Observable<ResponseData<IClaim>>;
  }

  // getCreatedPreauthorizedClaimInfo(
  //   providerSlug: string,
  //   claimId: number,
  //   token: string
  // ): Observable<ResponseData<IClaim>> {
  //   return this.http.get(
  //     `${this.apiUrl}/reclamo-preautorizado/consultar/${providerSlug}/${claimId}`, //todo
  //     {
  //       headers: { token: token },
  //     }
  //   ) as Observable<ResponseData<IClaim>>;
  // }

  uploadClaimFiles(
    claimFiles: IUploadClaimFile[],
    token: string
  ): Observable<ResponseData<IUploadClaimFileResponse>> {
    return this.http.post(
      `${this.apiUrl}/api/v1/adjuntar-documento`,
      claimFiles,
      {
        headers: { token: token },
      }
    ) as Observable<ResponseData<IUploadClaimFileResponse>>;
  }

  getClaimFile(
    documentId: string,
    serviceType: 'OUTPATIENT' | 'PROC_LAB_IMAG',
    token: string,
    providerSlug?: string
  ): Observable<ResponseData<any>> {
    return this.http.get(
      `${this.apiUrl}/api/v1/visualizar-documento/${serviceType}/${documentId}?providerSlug=${providerSlug}`,
      {
        headers: { token: token },
      }
    ) as Observable<ResponseData<any>>;
  }
}
